import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Lock } from "lucide-react";
import { PRICING, BRAND } from "@/data/content";
import { track } from "@/lib/analytics";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6 },
};

export default function PricingSnapshot() {
  const tiers = PRICING.filter((t) => t.id !== "enterprise");
  const enterprise = PRICING.find((t) => t.id === "enterprise");

  return (
    <section
      id="checkout"
      data-testid="home-checkout-section"
      className="py-20 sm:py-28 px-5 sm:px-6 lg:px-12"
    >
      <div className="max-w-[1100px] mx-auto">
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto">
          <p className="overline">Easy checkout</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.08] mt-5 text-neutral-900">
            Pick a plan. Be inside
            <br className="hidden sm:block" /> in <span className="text-aurora">under a minute.</span>
          </h2>
          <p className="text-base text-neutral-500 leading-relaxed mt-5">
            Secure Stripe checkout with card, Apple Pay or Google Pay. No free
            trial - full access the moment payment clears.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12 sm:mt-14">
          {tiers.map((tier, i) => (
            <motion.article
              key={tier.id}
              {...fadeUp}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className={`p-8 sm:p-10 flex flex-col bg-white ${
                tier.featured
                  ? "filigree-border relative"
                  : "border border-neutral-200"
              }`}
              data-testid={`home-checkout-${tier.id}`}
            >
              {tier.featured && (
                <span className="absolute -top-3 left-8 bg-aurora text-white text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1">
                  Most popular
                </span>
              )}
              <p className="overline">{tier.name}</p>
              <div className="flex items-baseline gap-2 mt-4">
                <span className="font-serif text-5xl sm:text-6xl leading-none text-aurora-static">{tier.price}</span>
                <span className="text-xs uppercase tracking-[0.18em] text-neutral-400">{tier.cadence}</span>
              </div>
              <p className="text-sm text-neutral-500 leading-relaxed mt-4">{tier.tagline}</p>
              <ul className="mt-6 space-y-2.5 flex-1">
                {tier.features.slice(0, 5).map((f) => (
                  <li key={f} className="flex gap-2.5 items-start text-sm text-neutral-700">
                    <Check size={14} className="flex-none mt-0.5 text-gold" strokeWidth={2.4} />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href={tier.href || "/pricing"}
                target={tier.href ? "_blank" : "_self"}
                rel="noreferrer"
                onClick={() => track("Home Checkout Clicked", { tier: tier.id })}
                data-testid={`home-checkout-cta-${tier.id}`}
                className={`${tier.featured ? "btn-aurora" : "btn-ghost"} mt-8 w-full justify-center`}
              >
                {tier.cta} <ArrowUpRight size={14} />
              </a>
            </motion.article>
          ))}
        </div>

        <motion.div
          {...fadeUp}
          className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 border border-neutral-200 bg-[#FAF8F2] px-6 sm:px-8 py-5"
          data-testid="home-checkout-enterprise"
        >
          <p className="text-sm text-neutral-600">
            <span className="font-semibold text-neutral-900">Need Enterprise?</span>{" "}
            {enterprise?.tagline}
          </p>
          <a
            href={BRAND.calendly}
            target="_blank"
            rel="noreferrer"
            onClick={() => track("Home Checkout Clicked", { tier: "enterprise" })}
            className="text-xs uppercase tracking-[0.2em] text-gold hover:text-gold-deep transition flex items-center gap-2 flex-none"
          >
            Book a call <ArrowUpRight size={13} />
          </a>
        </motion.div>

        <p className="mt-6 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] text-neutral-400 text-center">
          <Lock size={11} className="text-gold" />
          Secured by Stripe · Cancel anytime · Annual saves 20% on the <Link to="/pricing" className="underline underline-offset-2 hover:text-gold transition">pricing page</Link>
        </p>
      </div>
    </section>
  );
}
